import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { formatUnits } from 'viem';

import type { AssetId } from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import { Button, Content, Footer } from '@infinex/ui/components';

import { getTRPCClient } from '../../../api/config';
import { GAS_DEPOSIT_TRANSFER_KEY } from '../../../api/gas/useGasDepositComplete';
import { useWithdrawalsQuery } from '../../../api/withdrawals/withdrawalQueries';
import type { CustodyIconType } from '../../../utils/custodyIcon';
import { TransactionProcessing } from '../../transaction-processing/TransactionProcessing';
import { useCurrencyUnit } from '../../unit-switch';
import { useCustodySelection } from '../../wallet-selector/WalletSelectedProvider';
import { useGasDeposit } from '../GasDepositQuoteProvider';

const useMovingFundsStatus = (withdrawalId?: string) => {
  const { data: withdrawals } = useWithdrawalsQuery({
    refetchInterval: 5 * 1000,
    refetchOnMount: 'always',
  });

  const currentWithdrawal = withdrawals?.find((w) => w.id === withdrawalId);

  if (!currentWithdrawal) {
    return { status: 'pending' as const };
  }

  const { status } = currentWithdrawal;
  if (status === 'created' || status === 'pending') {
    return { status: 'pending' as const };
  }

  return { status };
};

export type MovingFundsProps = {
  chainKey: ChainKey;
  assetId: AssetId;
  symbol: string;
  usdRate: number;
  tokenValue: bigint;
  tokenDecimals: number;
  status: 'pending' | 'success' | 'error';
  walletName: string;
  custodyIconType: CustodyIconType;
  walletId?: string;
};

const GAS_STATUS_LABELS = {
  pending: 'Topping up',
  success: 'Topped up',
  error: 'Could not top up',
  review: 'Review',
  partial: 'Partially topped up',
} as const;

export const MovingFundsInner = ({
  symbol,
  chainKey,
  assetId,
  usdRate,
  tokenValue,
  tokenDecimals,
  status,
  walletName,
  custodyIconType,
  walletId,
}: MovingFundsProps) => {
  const amount = formatUnits(tokenValue, tokenDecimals);
  return (
    <>
      <Content className="flex-col-fill">
        <TransactionProcessing
          status={status}
          statusLabels={GAS_STATUS_LABELS}
          gas="free"
          from={{
            label: walletName,
            type: custodyIconType,
            walletId,
          }}
          to={{
            label: 'Fee account',
            type: 'gas-account',
          }}
          asset={{
            assetId,
            chain: chainKey,
            assetType: 'token',
            decimals: tokenDecimals,
          }}
          symbol={symbol}
          amount={amount}
          priceData={{ usd: { rate: usdRate } }}
        />
      </Content>

      <Footer>
        <Button
          size="large"
          className="w-full"
          prominence={status === 'success' ? 'high' : 'default'}
          slot="close"
        >
          Close
        </Button>
      </Footer>
    </>
  );
};

export const MovingFunds = () => {
  const data = useGasDeposit();
  const trpc = getTRPCClient();
  const gasQueryKey = trpc.gas.getGasActivities.queryOptions().queryKey;
  const initialData = useRef(data);
  const { assetSelection, gasQuote } = initialData.current;

  const { selectedAssetDetails } = assetSelection;

  const queryClient = useQueryClient();
  const transferId = queryClient.getQueryData<string>([
    GAS_DEPOSIT_TRANSFER_KEY,
  ]);

  const { status } = useMovingFundsStatus(transferId);
  useEffect(() => {
    if (status === 'error' || status === 'success') {
      void queryClient.invalidateQueries({
        queryKey: gasQueryKey,
      });
    }
  }, [queryClient, status, gasQueryKey]);

  const { selected } = useCustodySelection();
  const walletName = selected?.label;
  const { usdValue: usdAmount } = useCurrencyUnit();

  if (!walletName) return null;

  if (!selectedAssetDetails || !usdAmount) {
    return null;
  }
  const { chain, assetId, symbol } = selectedAssetDetails;

  if (!assetId) return null;

  if (gasQuote?.status !== 'succeeded') return null;

  // Derive custody icon type from selected custody
  const custodyIconType: CustodyIconType =
    selected.custody === 'vault'
      ? 'vault'
      : selected.isImported
        ? 'imported'
        : 'infinex';

  return (
    <MovingFundsInner
      chainKey={chain}
      assetId={assetId}
      symbol={symbol}
      tokenValue={BigInt(gasQuote.assetAmount)}
      tokenDecimals={gasQuote.assetDecimals}
      usdRate={gasQuote.usdRate}
      status={status}
      walletName={walletName}
      custodyIconType={custodyIconType}
      walletId={selected.custody === 'vault' ? 'vault' : selected.id}
    />
  );
};
